import { openSearch, type PieceSymbol, type SearchMove, type SearchPosition } from './chess';
import { BOT_CHESS_DEPTH, BOT_CHESS_QUIESCENCE_PLIES, BOT_CHESS_VARIETY_CP } from './constants';
import { hashRandom } from './rng';
import type { ChessMove, State } from './types';

// The chess bot: a small deterministic search and a pure function of the state (rule 1): no clock, no
// memory, no Math.random. Negamax with alpha-beta over BOT_CHESS_DEPTH plies, then a quiescence search
// of captures so that the horizon does not hand out "free" material, material plus piece-square tables
// as the evaluation, mate above everything. Near-equal moves are decided by a hash of the state's RNG
// and the tick, so the bot varies its play throughout the game and still replays identically.

/** centipawns; the king has no price, losing it is mate */
const VALUE: Record<PieceSymbol, number> = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 0 };
/** with no more than this much non-pawn material on the board, the king table switches to the endgame */
const ENDGAME_MATERIAL = 1300;
/** a mate found at ply n scores MATE - n: the quicker mate wins, the slower loss is preferred */
const MATE = 100_000;
const INF = 1_000_000;

// Piece-square tables after Tomasz Michniewski's "simplified evaluation function"
// (chessprogramming.org), from white's point of view with rank 8 first; mirrored for black.
// prettier-ignore
const PAWN = [
   0,  0,  0,  0,  0,  0,  0,  0,
  50, 50, 50, 50, 50, 50, 50, 50,
  10, 10, 20, 30, 30, 20, 10, 10,
   5,  5, 10, 25, 25, 10,  5,  5,
   0,  0,  0, 20, 20,  0,  0,  0,
   5, -5,-10,  0,  0,-10, -5,  5,
   5, 10, 10,-20,-20, 10, 10,  5,
   0,  0,  0,  0,  0,  0,  0,  0,
];
// prettier-ignore
const KNIGHT = [
  -50,-40,-30,-30,-30,-30,-40,-50,
  -40,-20,  0,  0,  0,  0,-20,-40,
  -30,  0, 10, 15, 15, 10,  0,-30,
  -30,  5, 15, 20, 20, 15,  5,-30,
  -30,  0, 15, 20, 20, 15,  0,-30,
  -30,  5, 10, 15, 15, 10,  5,-30,
  -40,-20,  0,  5,  5,  0,-20,-40,
  -50,-40,-30,-30,-30,-30,-40,-50,
];
// prettier-ignore
const BISHOP = [
  -20,-10,-10,-10,-10,-10,-10,-20,
  -10,  0,  0,  0,  0,  0,  0,-10,
  -10,  0,  5, 10, 10,  5,  0,-10,
  -10,  5,  5, 10, 10,  5,  5,-10,
  -10,  0, 10, 10, 10, 10,  0,-10,
  -10, 10, 10, 10, 10, 10, 10,-10,
  -10,  5,  0,  0,  0,  0,  5,-10,
  -20,-10,-10,-10,-10,-10,-10,-20,
];
// prettier-ignore
const ROOK = [
   0,  0,  0,  0,  0,  0,  0,  0,
   5, 10, 10, 10, 10, 10, 10,  5,
  -5,  0,  0,  0,  0,  0,  0, -5,
  -5,  0,  0,  0,  0,  0,  0, -5,
  -5,  0,  0,  0,  0,  0,  0, -5,
  -5,  0,  0,  0,  0,  0,  0, -5,
  -5,  0,  0,  0,  0,  0,  0, -5,
   0,  0,  0,  5,  5,  0,  0,  0,
];
// prettier-ignore
const QUEEN = [
  -20,-10,-10, -5, -5,-10,-10,-20,
  -10,  0,  0,  0,  0,  0,  0,-10,
  -10,  0,  5,  5,  5,  5,  0,-10,
   -5,  0,  5,  5,  5,  5,  0, -5,
    0,  0,  5,  5,  5,  5,  0, -5,
  -10,  5,  5,  5,  5,  5,  0,-10,
  -10,  0,  5,  0,  0,  0,  0,-10,
  -20,-10,-10, -5, -5,-10,-10,-20,
];
// prettier-ignore
const KING_MIDDLE = [
  -30,-40,-40,-50,-50,-40,-40,-30,
  -30,-40,-40,-50,-50,-40,-40,-30,
  -30,-40,-40,-50,-50,-40,-40,-30,
  -30,-40,-40,-50,-50,-40,-40,-30,
  -20,-30,-30,-40,-40,-30,-30,-20,
  -10,-20,-20,-20,-20,-20,-20,-10,
   20, 20,  0,  0,  0,  0, 20, 20,
   20, 30, 10,  0,  0, 10, 30, 20,
];
// prettier-ignore
const KING_END = [
  -50,-40,-30,-20,-20,-30,-40,-50,
  -30,-20,-10,  0,  0,-10,-20,-30,
  -30,-10, 20, 30, 30, 20,-10,-30,
  -30,-10, 30, 40, 40, 30,-10,-30,
  -30,-10, 30, 40, 40, 30,-10,-30,
  -30,-10, 20, 30, 30, 20,-10,-30,
  -30,-30,  0,  0,  0,  0,-30,-30,
  -50,-30,-30,-30,-30,-30,-30,-50,
];
const TABLE: Record<Exclude<PieceSymbol, 'k'>, readonly number[]> = { p: PAWN, n: KNIGHT, b: BISHOP, r: ROOK, q: QUEEN };

/** Material and placement from the side to move's point of view. */
function evaluate(pos: SearchPosition): number {
  const pieces = pos.pieces();
  let material = 0;
  for (const p of pieces) if (p.type !== 'p' && p.type !== 'k') material += VALUE[p.type];
  const king = material <= ENDGAME_MATERIAL ? KING_END : KING_MIDDLE;
  let score = 0;
  for (const p of pieces) {
    const table = p.type === 'k' ? king : TABLE[p.type];
    const index = (p.color === 'w' ? 7 - p.rank : p.rank) * 8 + p.file;
    const value = VALUE[p.type] + table[index];
    score += p.color === 'w' ? value : -value;
  }
  return pos.turn() === 'w' ? score : -score;
}

/** Captures first, the most valuable victim with the least valuable attacker ahead; promotions among them. */
function orderKey(m: SearchMove): number {
  let key = 0;
  if (m.captured) key += 1000 + 10 * VALUE[m.captured] - (m.piece === 'k' ? 1000 : VALUE[m.piece]);
  if (m.promotion) key += 500 + VALUE[m.promotion];
  return key;
}

/** Stable, so quiet moves keep chess.js's generation order and the search stays deterministic. */
function ordered(moves: SearchMove[]): SearchMove[] {
  return moves.sort((a, b) => orderKey(b) - orderKey(a));
}

interface Search {
  pos: SearchPosition;
  nodes: number;
}

function hasLegalMove(pos: SearchPosition): boolean {
  for (const m of pos.moves()) {
    if (pos.tryMake(m)) {
      pos.undo();
      return true;
    }
  }
  return false;
}

/**
 * Past the horizon only captures and promotions are searched, standing pat on the static evaluation
 * otherwise, for at most BOT_CHESS_QUIESCENCE_PLIES plies. In check there is no standing pat: every
 * move is tried, so a mate at the horizon is seen and not glossed over by the evaluation.
 */
function quiesce(S: Search, ply: number, qply: number, alpha: number, beta: number): number {
  S.nodes++;
  const pos = S.pos;
  const inCheck = pos.inCheck();
  const atCap = qply >= BOT_CHESS_QUIESCENCE_PLIES;
  if (inCheck && atCap) return hasLegalMove(pos) ? evaluate(pos) : -(MATE - ply);
  let best = -INF;
  let candidates: SearchMove[];
  if (inCheck) {
    candidates = pos.moves();
  } else {
    best = evaluate(pos);
    if (atCap || best >= beta) return best;
    candidates = pos.moves().filter((m) => m.captured || m.promotion);
  }
  if (best > alpha) alpha = best;
  let legal = 0;
  for (const m of ordered(candidates)) {
    if (!pos.tryMake(m)) continue;
    legal++;
    const score = -quiesce(S, ply + 1, qply + 1, -beta, -alpha);
    pos.undo();
    if (score > best) best = score;
    if (best > alpha) alpha = best;
    if (alpha >= beta) break;
  }
  if (inCheck && legal === 0) return -(MATE - ply);
  return best;
}

/** Fail-soft negamax with alpha-beta; exact for values inside (alpha, beta), a bound outside. */
function negamax(S: Search, depth: number, ply: number, alpha: number, beta: number): number {
  S.nodes++;
  const pos = S.pos;
  if (pos.insufficientMaterial()) return 0;
  if (depth <= 0) return quiesce(S, ply, 0, alpha, beta);
  let best = -INF;
  let legal = 0;
  for (const m of ordered(pos.moves())) {
    if (!pos.tryMake(m)) continue;
    legal++;
    const score = -negamax(S, depth - 1, ply + 1, -beta, -alpha);
    pos.undo();
    if (score > best) best = score;
    if (best > alpha) alpha = best;
    if (alpha >= beta) break;
  }
  if (legal === 0) return pos.inCheck() ? -(MATE - ply) : 0;
  return best;
}

export interface ChessSearch {
  /** null only when the side to move has no legal move, which the match has then already decided */
  move: ChessMove | null;
  /** selected move's score from the side to move's point of view; ±(MATE - plies) for a forced mate */
  score: number;
  /** positions visited; a pure function of the state like the move, so a test can hold the budget */
  nodes: number;
}

/**
 * A near-best move for the side to move in `s`. Opening the root window one point below the variety
 * band gives exact scores for all eligible moves; fail-low bounds cannot enter the final selection.
 */
export function searchChessMove(s: State, depth = BOT_CHESS_DEPTH): ChessSearch {
  const pos = openSearch(s.chess.fen);
  const S: Search = { pos, nodes: 1 };
  let best = -INF;
  const candidates: { move: SearchMove; score: number }[] = [];
  for (const m of ordered(pos.moves())) {
    if (!pos.tryMake(m)) continue;
    const score = -negamax(S, depth - 1, 1, -INF, -(best - BOT_CHESS_VARIETY_CP - 1));
    pos.undo();
    if (score > best) best = score;
    if (score >= best - BOT_CHESS_VARIETY_CP) candidates.push({ move: m, score });
  }
  if (candidates.length === 0) return { move: null, score: 0, nodes: S.nodes };
  // Preserve both the quickest forced win and the longest resistance to a forced loss.
  const tolerance = Math.abs(best) >= MATE / 2 ? 0 : BOT_CHESS_VARIETY_CP;
  const choices = candidates.filter((candidate) => candidate.score >= best - tolerance);
  const pick = choices[Math.floor(hashRandom(s.rng, s.tick * 13 + 2000) * choices.length)];
  const move: ChessMove = { from: pick.move.from, to: pick.move.to };
  if (pick.move.promotion) move.promotion = pick.move.promotion;
  return { move, score: pick.score, nodes: S.nodes };
}

/** The bot's move for the side to move, or null if there is none. */
export function chessBotMove(s: State): ChessMove | null {
  return searchChessMove(s).move;
}
